import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeSlash, CheckCircle, Warning } from '@phosphor-icons/react';
import toast from 'react-hot-toast';
import { LuminexLockup } from '../components/LuminexLogo';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PW_RULE = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

/**
 * Page that consumes a `?token=` password-reset link from the email.
 * On submit, calls POST /api/auth/reset-password. On success, redirects
 * to /login. Mirrors the AcceptInvitePage layout so the flow feels
 * consistent (same right-side card, same password-strength meter).
 */
export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    if (!token) { toast.error('Missing reset token'); return; }
    if (!PW_RULE.test(password)) {
      toast.error('Password must be 8+ chars with upper, lower, digit, and special.');
      return;
    }
    if (password !== confirm) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const resp = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password, confirmPassword: confirm }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.detail || 'Failed to reset password');
      setDone(true);
      toast.success('Password reset — you can sign in now');
    } catch (err) {
      const msg = err.message || 'Failed to reset password';
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="reset-password-page" style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'var(--bg-0)', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 460, background: 'var(--bg-1)', border: '1px solid var(--border-1)', borderRadius: 12, padding: '40px 36px' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
          <LuminexLockup iconSize={32} />
        </div>

        {done ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ display: 'inline-flex', width: 56, height: 56, borderRadius: '50%', background: 'rgba(14,124,107,0.12)', color: '#0E7C6B', alignItems: 'center', justifyContent: 'center', marginBottom: 18 }}>
              <CheckCircle size={32} weight="duotone" />
            </div>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 600, color: 'var(--text-1)', marginBottom: 8, letterSpacing: '-0.01em' }}>Password updated</h2>
            <p style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 24 }}>Your new password is now active. Sign in to continue.</p>
            <button className="btn btn-primary btn-block" onClick={() => navigate('/login')} data-testid="reset-goto-login-btn">Sign in</button>
          </div>
        ) : (
          <>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 600, color: 'var(--text-1)', marginBottom: 6, letterSpacing: '-0.01em', textAlign: 'center' }}>Choose a new password</h2>
            <p style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 24, textAlign: 'center' }}>Pick something strong &mdash; you&apos;ll use this password every time you sign in to Luminex.</p>

            {!token && (
              <div data-testid="reset-missing-token" style={{ display: 'flex', gap: 8, padding: 12, background: 'var(--danger-dim, rgba(122,31,31,0.08))', borderRadius: 8, marginBottom: 16 }}>
                <Warning size={18} weight="duotone" style={{ color: '#7A1F1F', flexShrink: 0, marginTop: 1 }} />
                <div style={{ fontSize: 12, color: '#7A1F1F', lineHeight: 1.5 }}>
                  This reset link is incomplete. Please request a new one from the sign-in page.
                </div>
              </div>
            )}

            {errorMsg && (
              <div data-testid="reset-error" style={{ padding: '8px 12px', background: 'var(--danger-dim, rgba(122,31,31,0.08))', borderRadius: 8, marginBottom: 12, fontSize: 12, color: '#7A1F1F' }}>
                {errorMsg}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">New Password</label>
                <div style={{ position: 'relative' }}>
                  <Lock size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }} />
                  <input type={showPw ? 'text' : 'password'} className="input" style={{ paddingLeft: 32, paddingRight: 36, fontSize: 13 }}
                    placeholder="Min 8 chars · upper · lower · digit · special"
                    value={password} onChange={e => setPassword(e.target.value)} required minLength={8} data-testid="reset-password-input" />
                  <button type="button" onClick={() => setShowPw(!showPw)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)' }} data-testid="reset-toggle-pw-btn">
                    {showPw ? <EyeSlash size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {password && (
                  <div data-testid="reset-password-strength" style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                    {[
                      { k: '8+ chars', pass: password.length >= 8 },
                      { k: 'A-Z', pass: /[A-Z]/.test(password) },
                      { k: 'a-z', pass: /[a-z]/.test(password) },
                      { k: '0-9', pass: /\d/.test(password) },
                      { k: 'special', pass: /[^A-Za-z0-9]/.test(password) },
                    ].map(c => (
                      <span key={c.k} style={{ fontSize: 10, padding: '2px 7px', borderRadius: 100, background: c.pass ? 'rgba(14,124,107,0.12)' : 'var(--bg-3)', color: c.pass ? '#0E7C6B' : 'var(--text-3)', fontWeight: 600 }}>
                        {c.pass ? '✓' : '·'} {c.k}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Confirm New Password</label>
                <div style={{ position: 'relative' }}>
                  <Lock size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }} />
                  <input type={showPw ? 'text' : 'password'} className="input" style={{ paddingLeft: 32, fontSize: 13 }}
                    placeholder="Re-enter your new password" value={confirm} onChange={e => setConfirm(e.target.value)} required data-testid="reset-confirm-password-input" />
                </div>
                {confirm && password !== confirm && (
                  <div style={{ fontSize: 11, color: '#7A1F1F', marginTop: 4 }}>Passwords don&apos;t match</div>
                )}
              </div>
              <button type="submit" className="btn btn-primary btn-block" disabled={loading || !token} data-testid="reset-submit-btn">
                {loading ? <span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> : 'Reset password'}
              </button>
              <p style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-2)', marginTop: 14 }}>
                Remembered it? <button type="button" onClick={() => navigate('/login')} style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontWeight: 600 }} data-testid="reset-back-to-login-btn">Back to sign in</button>
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
